// Monitoring-Tab "KI": GET /monitoring/llm — ab_suggest_results je Dokument (Bauplan: join
// ab_documents). Exaktes Response-Schema ist BE-1-Prosa, kein Code-Kontrakt -> defensiv lesen:
// bevorzugt vorab entpackte Felder (model/lineCount/matched), sonst best-effort JSON.parse von
// resultJson (Vorbild OC-Pilot pages/Monitoring.tsx LlmTab jobInfo()).

import React, { useCallback, useEffect, useState } from 'react';
import { Chip, Pager, fmtDateTime } from '../ui';
import { getMonitoring } from '../../api';

const PAGE_SIZE = 50;

function parseResult(r) {
  if (r.model !== undefined || r.lineCount !== undefined || r.matched !== undefined) {
    return { model: r.model, lineCount: r.lineCount, matched: r.matched };
  }
  if (typeof r.resultJson === 'string') {
    try {
      const d = JSON.parse(r.resultJson);
      return { model: d.model, lineCount: d.line_count ?? d.lineCount, matched: d.matched };
    } catch { /* kein verwertbares JSON */ }
  }
  return { model: undefined, lineCount: undefined, matched: undefined };
}

const AbMonitoringLlmTab = ({ user, isDarkMode, t, onOpenDocument, showError }) => {
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    getMonitoring(user, 'llm', { page, pageSize: PAGE_SIZE })
      .then((res) => { setItems(Array.isArray(res?.items) ? res.items : []); setTotal(Number(res?.total) || 0); setError(null); })
      .catch((e) => { setError(e.message); showError?.(e.message); })
      .finally(() => setLoading(false));
  }, [user, page, showError]);

  useEffect(() => { load(); }, [load]);

  const th = `text-left text-[11px] uppercase tracking-wide font-semibold px-3 py-2 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`;
  const td = `px-3 py-2.5 text-sm ${isDarkMode ? 'text-white/80' : 'text-slate-700'}`;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  if (loading && items.length === 0) return <p className={`text-sm py-8 text-center ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('ab.loading')}</p>;
  if (error && items.length === 0) return <p className={`text-sm py-6 text-center ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}>{t('ab.shell.monLoadError')}: {error}</p>;
  if (items.length === 0) return <p className={`text-sm py-8 text-center ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('ab.shell.monEmpty')}</p>;

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className={isDarkMode ? 'border-b border-white/10' : 'border-b border-slate-200'}>
              <th className={th}>{t('ab.shell.monColStarted')}</th>
              <th className={th}>{t('ab.colFile')}</th>
              <th className={th}>{t('ab.colSupplier')}</th>
              <th className={th}>{t('ab.shell.monColStatus')}</th>
              <th className={th}>{t('ab.shell.monColModel')}</th>
              <th className={`${th} text-right`}>{t('ab.colLines')}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((r, i) => {
              const parsed = parseResult(r);
              return (
                <tr key={r.documentId ?? i} className={isDarkMode ? 'border-b border-white/5' : 'border-b border-slate-100'}>
                  <td className={td}>{fmtDateTime(r.startedAt)}</td>
                  <td className={`${td} max-w-[200px] truncate`} title={r.fileName || ''}>
                    {r.documentId
                      ? <button type="button" onClick={() => onOpenDocument(r.documentId)} className={isDarkMode ? 'text-indigo-300 hover:text-indigo-200' : 'text-indigo-600 hover:text-indigo-700'}>{r.fileName || t('ab.shell.monOpen')}</button>
                      : (r.fileName || '—')}
                  </td>
                  <td className={td}>{r.supplierId || t('ab.supplierUnknown')}</td>
                  <td className={td}>
                    <Chip tone={r.status === 'done' ? 'green' : r.status === 'error' ? 'red' : 'blue'} isDarkMode={isDarkMode}>
                      {r.status || '—'}
                    </Chip>
                  </td>
                  <td className={`${td} max-w-[140px] truncate`} title={parsed.model || ''}>{parsed.model || '—'}</td>
                  <td className={`${td} text-right tabular-nums`}>{parsed.lineCount ?? '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Pager page={page} totalPages={totalPages} total={total} onPage={setPage} isDarkMode={isDarkMode} t={t} />
    </div>
  );
};

export default AbMonitoringLlmTab;
